import { useCallback, useMemo, useState } from 'react';
import { ButtonVariants, Form, FormSizes, Modal, ModalSizes } from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { useCreateMbPeriodMutation, useUpdateMbPeriodMutation } from '../../api/sitesApi';
import { EMPTY_MB_PERIOD_FORM, MB_PERIOD_FIELDS } from '../../constants/sites.constants';
import { buildMbPeriodDto } from '../../mappers/sitesMappers';
import { validateMbPeriodSequence } from '../../utils/sitesUtils';
import { MbPeriodModalFields } from './MbPeriodModalFields';
import styles from './MassBalancePeriodModal.module.scss';

/**
 * Props:
 *   open        whether the modal is shown
 *   onClose     close handler (✕ / Cancel)
 *   site        the site detail view model — names the form and supplies the terminals
 *   terminalId  the terminal the tab is scoped to, preselected when creating
 *   mbPeriod    the period being edited; omit to configure a new one
 */
export const MassBalancePeriodModal = ({ open, onClose, site, terminalId, mbPeriod }) => {
  const t = useTranslations();
  const [createMbPeriod, { isLoading: isCreating }] = useCreateMbPeriodMutation();
  const [updateMbPeriod, { isLoading: isUpdating }] = useUpdateMbPeriodMutation();
  const [errorKey, setErrorKey] = useState(null);

  const isEdit = Boolean(mbPeriod);
  const saving = isCreating || isUpdating;

  const initialValues = useMemo(() => {
    const base = mbPeriod?.formValues ?? {
      ...EMPTY_MB_PERIOD_FORM,
      [MB_PERIOD_FIELDS.terminalIds]: terminalId ? [String(terminalId)] : [],
    };
    return { ...base, [MB_PERIOD_FIELDS.siteName]: site.siteName };
  }, [mbPeriod, site.siteName, terminalId]);

  const terminalOptions = useMemo(
    () =>
      site.terminals.map((terminal) => ({
        value: String(terminal.id),
        label: terminal.terminalName,
      })),
    [site.terminals],
  );

  const handleClose = useCallback(() => {
    setErrorKey(null);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(
    async (values) => {
      const body = buildMbPeriodDto(values);

      if (!isEdit && body.terminalIds.length === 0) {
        setErrorKey('SITES_MB_PERIOD_ERROR_NO_TERMINAL');
        return;
      }

      const sequenceErrorKey = validateMbPeriodSequence(body.timePeriods);
      if (sequenceErrorKey) {
        setErrorKey(sequenceErrorKey);
        return;
      }

      setErrorKey(null);

      const result = isEdit
        ? await updateMbPeriod({ id: mbPeriod.id, siteId: site.id, values })
        : await createMbPeriod({ siteId: site.id, values });

      if (result.error) return;
      handleClose();
    },
    [isEdit, mbPeriod, site.id, createMbPeriod, updateMbPeriod, handleClose],
  );

  if (!open) return null;

  return (
    <Form
      size={FormSizes.medium}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      className={styles.form}
    >
      {({ submit }) => (
        <Modal
          open
          size={ModalSizes.medium}
          title={isEdit ? t('SITES_MB_PERIOD_EDIT_TITLE') : t('SITES_MB_PERIOD_ACTION_UPDATE')}
          onClose={handleClose}
          buttons={[
            { label: t('COMMON_CANCEL'), variant: ButtonVariants.neutral, onClick: handleClose },
            {
              label: t('COMMON_UPDATE'),
              variant: ButtonVariants.primary,
              disabled: saving,
              onClick: submit,
            },
          ]}
        >
          <MbPeriodModalFields
            terminalOptions={terminalOptions}
            terminalsLocked={isEdit}
            errorKey={errorKey}
            saving={saving}
          />
        </Modal>
      )}
    </Form>
  );
};
